import {
  IconCheck,
  Heading,
  LayoutVerticalSpacer,
  useMediumFromMediaQuery,
} from '@madpaws/design-system';
import DOMPurify from 'isomorphic-dompurify';
import React from 'react';

import { Card } from '~/components/pages/SearchResults/Card/Card';
import { SearchSeoProgrammaticContent } from '~/components/pages/SearchResults/SearchSeoProgrammaticContent/SearchSeoProgrammaticContent';
import { SERVICE_TYPES_SUPPORTING_AREA_10 } from '~/utils/area10ProgramaticContent/constants';

import styles from './SeoFooterContent.module.css';
import { DefaultDynamicSeoFooterContent } from '../DefaultDynamicSeoFooterContent/DefaultDynamicSeoFooterContent';

import type { DynamicSearchContent, SearchFilters } from '~/common/types/search';

type Props = {
  content?: DynamicSearchContent;
  heading?: string;
  items?: string[];
  mainContent?: string;
  postContent?: string;
  searchFilters?: SearchFilters;
};

export const SeoFooterContent = ({
  content,
  heading,
  items,
  mainContent,
  postContent,
  searchFilters,
}: Props): React.ReactElement => {
  const isMediumFromViewport = useMediumFromMediaQuery();

  if (content && searchFilters) {
    const {
      location: {
        address: { stateName, suburb, postcode },
      },
    } = searchFilters;

    const { contentList, header, serviceName } = content;
    const dynamicHeading = `Need more information about ${serviceName} in ${suburb} ${stateName}?`;
    const shouldRenderArea10SeoContent = SERVICE_TYPES_SUPPORTING_AREA_10.includes(serviceName);

    if (shouldRenderArea10SeoContent) {
      const id = `${suburb}-${stateName}-${postcode}`.replace(/\s/g, '-').toLowerCase();

      return (
        <SearchSeoProgrammaticContent
          articleId={id}
          fallbackContent={
            <DefaultDynamicSeoFooterContent
              contentList={contentList}
              header={header}
              heading={dynamicHeading}
            />
          }
          heading={dynamicHeading}
        />
      );
    }

    return (
      <DefaultDynamicSeoFooterContent
        contentList={contentList}
        header={header}
        heading={dynamicHeading}
      />
    );
  }

  return (
    <Card>
      <LayoutVerticalSpacer space="large1x">
        <Heading size={isMediumFromViewport ? 'small2x' : 'small3x'}>{heading}</Heading>
      </LayoutVerticalSpacer>
      {mainContent && (
        <div
          className={styles.body}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mainContent) }}
        />
      )}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul className={styles.benefits} role="list">
        {items?.map((item, key) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={`item-${key}`}>
            <IconCheck size="medium" />
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item) }} />
          </li>
        ))}
      </ul>
      {postContent && (
        <div
          className={styles.postBody}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(postContent) }}
        />
      )}
    </Card>
  );
};
